import React, { useState, useEffect } from "react";
import { Icons } from "../components/common";
import CESmartBatchingModal from "../components/zip/CESmartBatchingModal";
import { API_BASE } from "../constants/urls";

const CEAssignmentPage = () => {
    const [pendingSubmissions, setPendingSubmissions] = useState([]);
    const [availableCEs, setAvailableCEs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showBatchingModal, setShowBatchingModal] = useState(false);
    const [selectedArticles, setSelectedArticles] = useState([]);


    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [pendingRes, cesRes] = await Promise.all([
                fetch(`${API_BASE}/api/admin/te-submissions/pending-ce`, { credentials: "include" }),
                fetch(`${API_BASE}/api/ce-assignments/available-ces`, { credentials: "include" })
            ]);

            if (pendingRes.ok && cesRes.ok) {
                const pendingData = await pendingRes.json();
                const cesData = await cesRes.json();
                setPendingSubmissions(pendingData.submissions || []);
                setAvailableCEs(cesData.ces || []);
            }
        } catch (error) {
            console.error("Error fetching CE assignment data:", error);
        } finally {
            setLoading(false);
        }
    };

    const toggleArticleSelection = (articleId) => {
        setSelectedArticles(prev =>
            prev.includes(articleId)
                ? prev.filter(id => id !== articleId)
                : [...prev, articleId]
        );
    };

    const selectAll = () => {
        if (selectedArticles.length === pendingSubmissions.length) {
            setSelectedArticles([]);
        } else {
            setSelectedArticles(pendingSubmissions.map(s => s.article_id));
        }
    };

    const handleOpenBatchingModal = () => {
        if (selectedArticles.length === 0) {
            alert("Please select at least one article to assign.");
            return;
        }
        setShowBatchingModal(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 mob-wrapper">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                            CE Assignment
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Assign articles submitted by TEs to Copy Editors
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={fetchData}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Refresh"
                        >
                            🔄 Refresh
                        </button>
                        <button
                            onClick={handleOpenBatchingModal}
                            disabled={selectedArticles.length === 0}
                            className={`px-6 py-2.5 rounded-xl font-bold flex items-center transition-all ${selectedArticles.length > 0
                                ? "bg-purple-600 text-white shadow-lg shadow-purple-200 hover:scale-105"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                }`}
                        >
                            <span className="mr-2">📝</span>
                            Assign Selected ({selectedArticles.length})
                        </button>
                    </div>
                </div>
            </div>

            {/* Submissions Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={selectedArticles.length === pendingSubmissions.length && pendingSubmissions.length > 0}
                                        onChange={selectAll}
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Article ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Journal
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    TE Submitter
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Submitted Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Drive Link
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {pendingSubmissions.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                        No pending TE submissions found for CE assignment.
                                    </td>
                                </tr>
                            ) : (
                                pendingSubmissions.map((submission) => (
                                    <tr key={submission.article_id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedArticles.includes(submission.article_id)}
                                                onChange={() => toggleArticleSelection(submission.article_id)}
                                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                            {submission.article_id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {submission.journal_code || "N/A"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{submission.te_username}</div>
                                            <div className="text-xs text-gray-500">ID: {submission.te_id}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {submission.submission_timestamp ? new Date(submission.submission_timestamp).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex px-2 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-800">
                                                TE Submitted
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <a
                                                href={submission.drive_file_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 font-medium underline"
                                            >
                                                View File
                                            </a>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Smart Batching Modal */}
            {showBatchingModal && (
                <CESmartBatchingModal
                    isOpen={showBatchingModal}
                    onClose={() => setShowBatchingModal(false)}
                    selectedSubmissions={pendingSubmissions.filter(s => selectedArticles.includes(s.article_id))}
                    availableCEs={availableCEs}
                    onAssignmentComplete={() => {
                        setShowBatchingModal(false);
                        setSelectedArticles([]);
                        fetchData();
                    }}
                />
            )}
        </div>
    );
};

export default CEAssignmentPage;
